import { useState, useEffect } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { SideDrawer } from '@/components';
import { getCRMContacts, createCRMContactFollowUp } from '../api';
import { CRMFollowUpForm } from './CRMFollowUpForm';
import { parseApiError } from '@/common/utils';

export function CRMFollowUpModal({
  isOpen,
  onClose,
  companyId,
  contactId: propContactId = null,
  onSuccess,
}) {
  const queryClient = useQueryClient();

  // Contact Selection
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactSearch, setContactSearch] = useState('');
  const [debouncedContactSearch, setDebouncedContactSearch] = useState('');
  const [isContactDropdownOpen, setIsContactDropdownOpen] = useState(false);

  // Reset when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedContact(null);
      setContactSearch('');
      setIsContactDropdownOpen(false);
    }
  }, [isOpen]);

  // Debounce contact search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedContactSearch(contactSearch.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [contactSearch]);

  // Fetch contacts for picker when contact is not preselected
  const shouldFetchContacts = isOpen && !propContactId;

  const {
    data: contactsData,
    isLoading: isContactsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: [
      'crm-followup-modal-contacts',
      companyId,
      debouncedContactSearch,
    ],
    queryFn: ({ pageParam = 1 }) =>
      getCRMContacts(companyId, {
        search: debouncedContactSearch || undefined,
        page: pageParam,
        page_size: 20,
      }),
    getNextPageParam: (lastPage) => {
      if (!lastPage?.next) return undefined;
      try {
        return new URL(lastPage.next).searchParams.get('page') || undefined;
      } catch {
        const match = String(lastPage.next).match(/[?&]page=(\d+)/);
        return match ? match[1] : undefined;
      }
    },
    enabled: !!companyId && shouldFetchContacts && isContactDropdownOpen,
  });

  const contactsList =
    contactsData?.pages?.flatMap((page) => page?.results || []) || [];

  const handleContactScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (
      scrollHeight - scrollTop <= clientHeight + 40 &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  // Mutation: Create Follow-up
  const createMutation = useMutation({
    mutationFn: ({ contactId, payload }) =>
      createCRMContactFollowUp({ companyId, contactId, payload }),
    onSuccess: () => {
      toast.success('Follow-up added successfully!');
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-follow-ups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-followups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-unified'] });
      onSuccess?.();
      onClose();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to add follow-up.');
    },
  });

  const handleFormSubmit = (payload) => {
    const targetContactId = propContactId || selectedContact?.id;
    if (!targetContactId) {
      toast.error('Please select a contact / lead first.');
      return;
    }
    createMutation.mutate({
      contactId: targetContactId,
      payload,
    });
  };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Follow-Up"
      width="xl"
      className="crm-followup-drawer"
      closeOnOutside={false}
    >
      <div className="flex flex-col gap-5 pt-1 font-sf-pro-display">
        {/* Contact Selector (when not pre-fixed) */}
        {!propContactId && (
          <div className="flex flex-col gap-1.5 relative">
            <label className="text-sm font-semibold text-[#1F2241]">
              Contact / Lead <span className="text-red-500">*</span>
            </label>
            {selectedContact ? (
              <div className="flex items-center justify-between p-3 rounded-xl bg-[#EBF4EA]/50 border border-[#50AB3D]/40">
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-semibold text-[#1F2241] truncate">
                    {selectedContact.name || 'Unnamed Contact'}
                  </span>
                  {selectedContact.phone && (
                    <span className="text-xs text-text-secondary">
                      {selectedContact.phone}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedContact(null);
                    setContactSearch('');
                  }}
                  className="text-xs font-semibold text-[#50AB3D] hover:underline shrink-0 ml-2"
                >
                  Change Contact
                </button>
              </div>
            ) : (
              <div className="relative">
                <input
                  type="text"
                  value={contactSearch}
                  onChange={(e) => {
                    setContactSearch(e.target.value);
                    setIsContactDropdownOpen(true);
                  }}
                  onFocus={() => setIsContactDropdownOpen(true)}
                  placeholder="Search contact by name or phone to assign follow-up..."
                  className="w-full h-11 px-4 rounded-xl bg-white border border-[#EBF4EA] text-[#1F2241] text-sm placeholder:text-[#868686] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40 transition-colors"
                />
                {isContactDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsContactDropdownOpen(false)}
                    />
                    <div
                      onScroll={handleContactScroll}
                      className="absolute left-0 right-0 top-full mt-1.5 max-h-48 overflow-y-auto bg-white border border-[#EBF4EA] rounded-xl shadow-lg z-50 py-1 divide-y divide-gray-100"
                    >
                      {isContactsLoading ? (
                        <div className="p-3 text-xs text-center text-text-muted">
                          Loading contacts...
                        </div>
                      ) : contactsList.length === 0 ? (
                        <div className="p-3 text-xs text-center text-text-muted">
                          No contacts found
                        </div>
                      ) : (
                        contactsList.map((contact) => (
                          <div
                            key={contact.id}
                            onClick={() => {
                              setSelectedContact(contact);
                              setIsContactDropdownOpen(false);
                            }}
                            className="px-4 py-2.5 hover:bg-[#EBF4EA]/40 cursor-pointer transition-colors flex flex-col"
                          >
                            <span className="text-sm font-medium text-[#1F2241]">
                              {contact.name || 'Unnamed'}
                            </span>
                            {contact.phone && (
                              <span className="text-xs text-text-secondary">
                                {contact.phone}
                              </span>
                            )}
                          </div>
                        ))
                      )}
                      {isFetchingNextPage && (
                        <div className="p-3 text-xs text-center text-text-muted">
                          Loading more...
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div className="pt-2 border-t border-border-default/60">
          <CRMFollowUpForm
            companyId={companyId}
            onSubmit={handleFormSubmit}
            isPending={createMutation.isPending}
            variant="drawer"
            submitDisabled={!propContactId && !selectedContact}
          />
        </div>
      </div>
    </SideDrawer>
  );
}
